/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { useEffect, useState, type RefObject } from 'react';

import { useRafState } from '../raf/use-raf-state';

/**
 * @summary scrolls to bottom
 * @param ref
 */
export const scrollToBottom = (ref?: RefObject<HTMLDivElement>) => {
  ref?.current?.scrollTo({
    top: ref?.current?.scrollHeight,
  });
};

/**
 * @summary scrolls to top
 * @param smooth
 */
export const scrollToTop = (smooth = true): void => {
  if (smooth) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } else {
    window.pageYOffset = 0;
    window.scrollY = 0;
  }
};

/**
 * @summary returns scrollTop and direction every frame when scroll event is executed
 */
export function useGlobalScrollTop(): {
  scrollTop: number;
  direction: 'BOTTOM' | 'IDLE' | 'TOP';
} {
  const [scrollTop, setScrollTop] = useRafState({
    currPosition: window.pageYOffset,
  });
  const [scrollDirection, setScrollDirection] = useState<'BOTTOM' | 'IDLE' | 'TOP'>('IDLE');

  useEffect(() => {
    const onScroll = () => {
      setScrollTop((prevState) => {
        const { pageYOffset } = window;

        if (pageYOffset === prevState.currPosition) {
          setScrollDirection('IDLE');
        } else if (pageYOffset > prevState.currPosition) {
          setScrollDirection('BOTTOM');
        } else if (pageYOffset < prevState.currPosition) {
          setScrollDirection('TOP');
        }

        return { currPosition: pageYOffset };
      });
    };

    window.addEventListener('scroll', onScroll, {
      capture: false,
      passive: true,
    });

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return { scrollTop: scrollTop.currPosition, direction: scrollDirection };
}

interface Coord {
  x: number;
  y: number;
}

// TOOD: add test
export function useElementScroll(ref: RefObject<HTMLElement>): Coord {
  const [state, setState] = useRafState<Coord>({
    x: 0,
    y: 0,
  });

  useEffect(() => {
    const handler = () => {
      if (ref.current) {
        setState({
          x: ref.current.scrollLeft,
          y: ref.current.scrollTop,
        });
      }
    };

    const targetElement = ref.current;

    if (targetElement && targetElement.addEventListener) {
      targetElement.addEventListener('scroll', handler, {
        capture: false,
        passive: true,
      });
    }

    return () => {
      if (targetElement && targetElement.removeEventListener) {
        targetElement.removeEventListener('scroll', handler);
      }
    };
  }, [ref]);

  return state;
}
